"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import LatestFinancialCard from "./LatestFinancialCard";
import { getFlipitNews } from "../../services/TradingMallService";
import type { FinancialNewsItem } from "../../interfaces/LatestFinancialResult.interface";
import { trackComponent } from "@repo/analytics";
import { dlEventConstants } from "@repo/analytics/data-layer.constants";
import { getCustomerIdForAnalytics } from "../../utils/analytics-utils";
import { selectIsAuthSuccessOrPerpetual, useAppSelector } from "@repo/web-store";

interface LatestFinancialResultClientMainProps {
  stocks: FinancialNewsItem[];
  initialShowMore?: boolean;
  initialShowLess?: boolean;
  sectionTitle?: string;
  componentPosition?: string;
}


type LatestFinancialScrollState = {
  scrollY: number;
  items: FinancialNewsItem[];
  page: number;
  showMore: boolean;
  showLess: boolean;
};

const LATEST_FINANCIAL_SCROLL_STATE_KEY = "latest_financial_scroll_state";

const LatestFinancialResultClientMain: React.FC<LatestFinancialResultClientMainProps> = ({
  stocks = [],
  initialShowMore = false,
  initialShowLess = false,
  sectionTitle,
  componentPosition,
}) => {
  const isLoggedIn = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const [items, setItems] = useState<FinancialNewsItem[]>(stocks);
  const [page, setPage] = useState(1);
  const [showMore, setShowMore] = useState(initialShowMore);
  const [showLess, setShowLess] = useState(initialShowLess);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollRestoredRef = useRef(false);

  const mapNewsToCardData = (item: FinancialNewsItem) => {
    const primaryCompany = item.companies?.[0];
    return {
      id: item.id,
      logo: item.banner_link ?? "",
      fallbackImage:
        "https://cms-assets.bajajfinserv.in/is/image/bajajfinancestage/stocksplaceholder",
      heading: item.title,
      symbol: primaryCompany?.symbol ?? "",
      perChange: primaryCompany?.percentage ?? 0,
      createdAt: item.created_at ?? "",
    };
  };

const saveScrollState = useCallback(
    (scrollY: number) => {
      const state: LatestFinancialScrollState = {
        scrollY,
        items,
        page,
        showMore,
        showLess,
      };
      sessionStorage.setItem(
        LATEST_FINANCIAL_SCROLL_STATE_KEY,
        JSON.stringify(state),
      );
    },
    [items, page, showLess, showMore],
  );

  const clearScrollState = useCallback(() => {
    sessionStorage.removeItem(LATEST_FINANCIAL_SCROLL_STATE_KEY);
  }, []);

  const readScrollState = useCallback((): LatestFinancialScrollState | null => {
    const raw = sessionStorage.getItem(LATEST_FINANCIAL_SCROLL_STATE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as LatestFinancialScrollState;
    } catch {
      return null;
    }
  }, []);

  const performScrollRestoration = useCallback(
    (scrollY: number) => {
      const pageHeight = document.documentElement.scrollHeight;
      const targetScroll = Math.min(scrollY, pageHeight - window.innerHeight);

      if (targetScroll > 0) {
        window.scrollTo({
          top: targetScroll,
          behavior: "instant" as ScrollBehavior,
        });
      }

      clearScrollState();
      scrollRestoredRef.current = true;
    },
    [clearScrollState],
  );

  const scheduleScrollRestoration = useCallback(
    (scrollY: number) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          performScrollRestoration(scrollY);
        });
      });
    },
    [performScrollRestoration],
  );

  useEffect(() => {
    if (scrollRestoredRef.current) return;

    const stored = readScrollState();
    if (!stored) {
      scrollRestoredRef.current = true;
      return;
    }

    // Restore list state first (so the clicked card exists), then restore scroll.
    setItems(stored.items ?? stocks);
    setPage(stored.page ?? 1);
    setShowMore(stored.showMore ?? initialShowMore);
    setShowLess(stored.showLess ?? initialShowLess);

    const timeoutId = setTimeout(() => {
      if (stored.scrollY > 0) {
        scheduleScrollRestoration(stored.scrollY);
      } else {
        clearScrollState();
        scrollRestoredRef.current = true;
      }
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [
    clearScrollState,
    initialShowLess,
    initialShowMore,
    readScrollState,
    scheduleScrollRestoration,
    stocks,
  ]);

  const handleToggle = async () => {
    // CT: show more/show less click (csv: ipo_card_sku_descriptive_button_click)
    void (async () => {
      try {
        const customerId = await getCustomerIdForAnalytics();
        const ctaText = showMore ? "SHOW MORE" : "SHOW LESS";
        await trackComponent({
          componentName: "ipo_card_sku_descriptive",
          sectionTitle: sectionTitle ?? "Latest financial results",
          eventType: dlEventConstants.CLICK,
          clickType: "ipo_card_sku_descriptive_button_click",
          ctaText,
          productCategory: "investment",
          cleverTapOverrides: {
            EP_REFERENCE_ID: "latest_financial_results",
            ...(customerId && { EP_CUSTOMER_ID: customerId }),
            ...(componentPosition && { EP_SR_NUMBER: componentPosition }),
            EP_ENTRYPOINT: "horizontal/entrypoint",
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track latest financial show more/less click", error);
      }
    })();

    if (showMore) {
      if (isLoadingMore) return;
      const nextPage = page + 1;
      setIsLoadingMore(true);
      try {
        const response = await getFlipitNews(nextPage, 10);
        const news = response?.data?.news ?? [];
        setItems((prev) => [...prev, ...news]);
        setPage(nextPage);
        setShowMore(response?.data?.showMore ?? false);
        setShowLess(response?.data?.showLess ?? false);
      } catch (error) {
        console.error("Failed to load more financial results:", error);
      } finally {
        setIsLoadingMore(false);
      }
      return;
    }

    if (showLess) {
      setItems(stocks);
      setPage(1);
      setShowMore(initialShowMore);
      setShowLess(initialShowLess);
    }
  };

  const LoaderCards = () => {
    return (
      <div className="flex flex-col gap-3 w-full max-w-[600px]">
        {Array.from({ length: 5 }).map((_, idx) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`latest-financial-skeleton-${idx}`}
            className="w-full min-h-[112px] border border-[#E5EAF7] rounded-[12px] p-4 flex gap-3"
          >
            <BFLSkeleton height={48} width={48} classes="rounded-[8px]" />
            <div className="flex flex-col gap-2 flex-1">
              <BFLSkeleton height={16} width="85%" classes="rounded-[6px]" />
              <BFLSkeleton height={16} width="60%" classes="rounded-[6px]" />
              <BFLSkeleton height={14} width="40%" classes="rounded-[6px]" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col gap-[12px]">
        {items.map((stock, index) => {
          const cardData = mapNewsToCardData(stock);
          return (
            <div
              key={cardData.id}
              onClick={() => saveScrollState(window.scrollY)}
              role="presentation"
            >
              <LatestFinancialCard
                className="max-w-[600px] min-h-[112px]"
                componentPosition={(index + 1).toString()}
                paragraphClassName="md:text-[10px]"
                {...cardData}
              />
            </div>
          );
        })}
      </div>
      {isLoggedIn && showMore && isLoadingMore && <LoaderCards />}
      {isLoggedIn && (showMore || showLess) && (
        <div className="flex justify-center mt-[20px]">
          <button
            type="button"
            onClick={handleToggle}
            disabled={showMore ? isLoadingMore : false}
            className={`h-[40px] whitespace-nowrap text-[14px] font-medium md:w-[258px] w-full text-[#FF6700] border border-[#FF6700] rounded-[23px] md:pt-[12px] md:pb-[11px] md:px-[87px] px-[16px] py-[11.5px] text-center cursor-pointer lg:hover:bg-[#FF6700] lg:hover:text-white ${showMore && isLoadingMore ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {showMore ? "SHOW MORE" : "SHOW LESS"}
          </button>
        </div>
      )}
    </>
  );
};

export default LatestFinancialResultClientMain;
